import { calendarDate } from '../utility/dateLocale';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Group,
  Title,
  Text,
  ScrollArea,
  Stack,
  SegmentedControl,
  Checkbox,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import {
  formKeysPresentInRow,
  mergeDefinedFields,
  readRowCell,
} from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { Options } from '../utility/types';
import InventoryInspectionFormsPanel from '../components/InventoryInspectionForms';
import {
  InventoryInspectionForms,
  buildDefaultInspectionForms,
  downloadInventoryDocument,
  emptyInspectionForms,
} from '../utility/inventoryInspection';
import { useMrtTableContainerProps, useModalScrollAreaHeight, usePageTableShellStyle } from '../utility/mobilePageLayout';

type InventoryStatus =
  | 'BLOCKED'
  | 'APPROVED'
  | 'DEMO'
  | 'IN_STOCK'
  | 'ALUGUEL'
  | 'COMODATO'
  | 'SOLD'
  | 'DELIVERED'
  | 'QUARANTINED'
  | 'NON_CONFORMING'
  | 'PENDING_VERIFICATION';

type ProductSummary = {
  id: string;
  name: string;
  model_number?: string | null;
  barcode?: string | null;
  manufacturer?: string | null;
  regulatory_class?: string | null;
  visual_validation?: boolean;
  functionality_verification?: boolean;
  accessories_check?: boolean;
};

type InventoryRow = {
  id: number;
  uuid: string;
  product_id: string;
  product_name: string;
  batch_number: string;
  serial_number: string;
  location: string;
  status: InventoryStatus;
  provider: string;
  manufacturer_invoice: string;
  remito: string;
  manufacture_date: string | null;
  expiration_date: string | null;
  quality_control_date: string | null;
  operation_verification_date: string | null;
  accessories_check_date: string | null;
  created_at: string;
  inspection_forms?: InventoryInspectionForms | null;
  deletedAt?: string | null;
};

type InventoryForm = {
  product_id: string;
  batch_number: string;
  serial_number: string;
  serial_number_end: string;
  location: string;
  status: InventoryStatus;
  provider: string;
  manufacturer_invoice: string;
  remito: string;
  manufacture_date: Date | null;
  expiration_date: Date | null;
  visual_validation_complete: boolean;
  visual_validation_date: Date | null;
  functionality_verification_complete: boolean;
  functionality_verification_date: Date | null;
  accessories_check_complete: boolean;
  accessories_check_date: Date | null;
};

type SerialInputMode = 'single' | 'range';

const maxSerialRangeSize = 200;

const initialForm: InventoryForm = {
  product_id: '',
  batch_number: '',
  serial_number: '',
  serial_number_end: '',
  location: '',
  status: 'APPROVED',
  provider: '',
  manufacturer_invoice: '',
  remito: '',
  manufacture_date: null,
  expiration_date: null,
  visual_validation_complete: false,
  visual_validation_date: null,
  functionality_verification_complete: false,
  functionality_verification_date: null,
  accessories_check_complete: false,
  accessories_check_date: null,
};

const remitoIncrementStatuses = new Set<InventoryStatus>(['DEMO', 'SOLD', 'ALUGUEL', 'COMODATO']);

function inventoryStatusLabel(t: TFunction, status: InventoryStatus | 'AVAILABLE') {
  return t(`inventory.status.${status}`);
}

function generalStatusOptions(t: TFunction): { value: InventoryStatus; label: string }[] {
  const values: InventoryStatus[] = [
    'APPROVED',
    'DEMO',
    'IN_STOCK',
    'ALUGUEL',
    'COMODATO',
    'BLOCKED',
    'SOLD',
    'DELIVERED',
    'QUARANTINED',
    'NON_CONFORMING',
  ];
  return values.map((value) => ({
    value,
    label: inventoryStatusLabel(t, value),
  }));
}

function productRequiresInspection(product: ProductSummary | undefined) {
  return Boolean(
    product?.visual_validation ||
      product?.functionality_verification ||
      product?.accessories_check,
  );
}

function allRequiredChecksComplete(form: InventoryForm, product: ProductSummary | undefined) {
  if (!productRequiresInspection(product) || !product) {
    return false;
  }
  if (product.visual_validation && !form.visual_validation_complete) {
    return false;
  }
  if (product.functionality_verification && !form.functionality_verification_complete) {
    return false;
  }
  if (product.accessories_check && !form.accessories_check_complete) {
    return false;
  }
  return true;
}

function hasPendingRequiredChecks(form: InventoryForm, product: ProductSummary | undefined) {
  return productRequiresInspection(product) && !allRequiredChecksComplete(form, product);
}

function applyInspectionStatusRules(
  form: InventoryForm,
  product: ProductSummary | undefined,
): InventoryForm {
  if (!productRequiresInspection(product)) {
    return form;
  }
  if (allRequiredChecksComplete(form, product) && form.status === 'PENDING_VERIFICATION') {
    return { ...form, status: 'APPROVED' };
  }
  if (hasPendingRequiredChecks(form, product)) {
    if (form.status === 'APPROVED') {
      return { ...form, status: 'PENDING_VERIFICATION' };
    }
    if (!['QUARANTINED', 'NON_CONFORMING', 'PENDING_VERIFICATION'].includes(form.status)) {
      return { ...form, status: 'PENDING_VERIFICATION' };
    }
  }
  return form;
}

function statusOptionsForContext(
  t: TFunction,
  product: ProductSummary | undefined,
  form: InventoryForm,
) {
  if (!productRequiresInspection(product) || allRequiredChecksComplete(form, product)) {
    return [...generalStatusOptions(t)];
  }

  const options: { value: InventoryStatus; label: string }[] = [
    { value: 'QUARANTINED', label: inventoryStatusLabel(t, 'QUARANTINED') },
    { value: 'NON_CONFORMING', label: inventoryStatusLabel(t, 'NON_CONFORMING') },
    { value: 'PENDING_VERIFICATION', label: inventoryStatusLabel(t, 'PENDING_VERIFICATION') },
  ];

  if (!options.some((option) => option.value === form.status)) {
    options.unshift({
      value: form.status,
      label: inventoryStatusLabel(t, form.status),
    });
  }

  return options;
}

function resolveStatusForProduct(
  t: TFunction,
  form: InventoryForm,
  product: ProductSummary | undefined,
) {
  const withRules = applyInspectionStatusRules(form, product);
  const options = statusOptionsForContext(t, product, withRules);
  const allowed = new Set(options.map((option) => option.value));
  if (allowed.has(withRules.status)) {
    return withRules;
  }
  const fallback: InventoryStatus = productRequiresInspection(product)
    ? hasPendingRequiredChecks(withRules, product)
      ? 'PENDING_VERIFICATION'
      : 'APPROVED'
    : 'APPROVED';
  return {
    ...withRules,
    status: allowed.has(fallback) ? fallback : options[0].value,
  };
}

function reconcileProductSelection(
  t: TFunction,
  prev: InventoryForm,
  product: ProductSummary | undefined,
  productId: string,
) {
  const cleared = clearDisabledInspectionFields({ ...prev, product_id: productId }, product);
  return resolveStatusForProduct(t, cleared, product);
}

function normalizeApiStatus(rawStatus: string): InventoryStatus {
  if (rawStatus === 'AVAILABLE') {
    return 'APPROVED';
  }
  return rawStatus as InventoryStatus;
}

function todayAtMidnight() {
  const value = new Date();
  value.setHours(0, 0, 0, 0);
  return value;
}

function resolveInspectionFields(item: {
  quality_control_date?: string | null;
  operation_verification_date?: string | null;
  accessories_check_date?: string | null;
}) {
  const visualComplete = Boolean(item.quality_control_date);
  const visualDate = parseApiDate(item.quality_control_date);
  const functionalityComplete = Boolean(item.operation_verification_date);
  const functionalityDate = parseApiDate(item.operation_verification_date);
  const accessoriesComplete = Boolean(item.accessories_check_date);
  const accessoriesDate = parseApiDate(item.accessories_check_date);

  return {
    visual_validation_complete: visualComplete,
    visual_validation_date: visualComplete ? visualDate ?? todayAtMidnight() : null,
    functionality_verification_complete: functionalityComplete,
    functionality_verification_date: functionalityComplete
      ? functionalityDate ?? todayAtMidnight()
      : null,
    accessories_check_complete: accessoriesComplete,
    accessories_check_date: accessoriesComplete
      ? accessoriesDate ?? todayAtMidnight()
      : null,
  };
}

function clearDisabledInspectionFields(
  form: InventoryForm,
  product: ProductSummary | undefined,
): InventoryForm {
  const next = { ...form };
  if (!product?.visual_validation) {
    next.visual_validation_complete = false;
    next.visual_validation_date = null;
  }
  if (!product?.functionality_verification) {
    next.functionality_verification_complete = false;
    next.functionality_verification_date = null;
  }
  if (!product?.accessories_check) {
    next.accessories_check_complete = false;
    next.accessories_check_date = null;
  }
  return next;
}

function validateInspectionChecks(
  t: TFunction,
  form: InventoryForm,
  product: ProductSummary | undefined,
): string | null {
  if (product?.visual_validation && form.visual_validation_complete && !form.visual_validation_date) {
    return t('inventory.visualValidationDateRequired');
  }
  if (
    product?.functionality_verification &&
    form.functionality_verification_complete &&
    !form.functionality_verification_date
  ) {
    return t('inventory.functionalityVerificationDateRequired');
  }
  if (product?.accessories_check && form.accessories_check_complete && !form.accessories_check_date) {
    return t('inventory.accessoriesCheckDateRequired');
  }
  return null;
}

function formatApiError(detail: unknown): string | null {
  if (typeof detail === 'string') {
    return detail;
  }
  // FastAPI 422 returns a list of {loc, msg, ...} validation errors.
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          const loc = Array.isArray((item as { loc?: unknown[] }).loc)
            ? (item as { loc: unknown[] }).loc.filter((part) => part !== 'body').join('.')
            : '';
          const msg = String((item as { msg: unknown }).msg);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return null;
      })
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join(' | ');
    }
  }
  return null;
}

function formatApiDate(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  return value.toISOString().split('T')[0];
}

function parseApiDate(value: string | null | undefined): Date | null {
  return calendarDate(value);
}

function usesRemito(status: InventoryStatus) {
  return remitoIncrementStatuses.has(status);
}

function digitsOnly(value: string) {
  return value.replace(/\D/g, '');
}

function expandSerialRange(
  t: TFunction,
  from: string,
  to: string,
):
  | { ok: true; serials: string[] }
  | { ok: false; message: string } {
  if (!from || !to) {
    return { ok: false, message: t('inventory.serialRangeBothRequired') };
  }
  if (from.length !== to.length) {
    return {
      ok: false,
      message: t('inventory.serialRangeSameLength'),
    };
  }
  const startNum = Number.parseInt(from, 10);
  const endNum = Number.parseInt(to, 10);
  if (Number.isNaN(startNum) || Number.isNaN(endNum)) {
    return { ok: false, message: t('inventory.serialDigitsOnly') };
  }
  if (endNum < startNum) {
    return { ok: false, message: t('inventory.serialRangeEndBeforeStart') };
  }
  const count = endNum - startNum + 1;
  if (count > maxSerialRangeSize) {
    return {
      ok: false,
      message: t('inventory.serialRangeMaxExceeded', { max: maxSerialRangeSize }),
    };
  }
  const serials: string[] = [];
  for (let number = startNum; number <= endNum; number += 1) {
    serials.push(String(number).padStart(from.length, '0'));
  }
  return { ok: true, serials };
}

function validateInventoryIdentifiers(
  t: TFunction,
  form: InventoryForm,
  serialInputMode: SerialInputMode,
  isEditing: boolean,
): string | null {
  if (form.batch_number && !/^\d+$/.test(form.batch_number)) {
    return t('inventory.lotDigitsOnly');
  }
  if (isEditing || serialInputMode === 'single') {
    if (form.serial_number && !/^\d+$/.test(form.serial_number)) {
      return t('inventory.serialNumberDigitsOnly');
    }
    return null;
  }
  if (!form.serial_number || !form.serial_number_end) {
    return t('inventory.serialRangeFieldsRequired');
  }
  const rangeResult = expandSerialRange(t, form.serial_number, form.serial_number_end);
  return rangeResult.ok ? null : rangeResult.message;
}

export default function InventoryPage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise } = useAuth();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const modalScrollAreaHeight = useModalScrollAreaHeight();

  const mrtLocalization = useMemo(
    () => buildMrtLocalization(t),
    [t, i18n.language],
  );

  const [inventoryRows, setInventoryRows] = useState<InventoryRow[]>([]);
  const [productCatalog, setProductCatalog] = useState<ProductSummary[]>([]);
  const [productOptions, setProductOptions] = useState<Options[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryRow | null>(null);
  const [inventoryForm, setInventoryForm] = useState<InventoryForm>(initialForm);
  const [errorMessage, setErrorMessage] = useState('');
  const [serialInputMode, setSerialInputMode] = useState<SerialInputMode>('single');
  const [inspectionForms, setInspectionForms] = useState<InventoryInspectionForms>(emptyInspectionForms());
  const [generatingDocument, setGeneratingDocument] = useState<string | null>(null);
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importNotification, setImportNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const mapInventoryRows = useCallback((data: unknown[]): InventoryRow[] => {
    return data.map((item: any, index: number) => ({
      id: index + 1,
      uuid: item.id,
      product_id: item.product_id,
      product_name: item.product?.name ?? '',
      batch_number: item.batch_number ?? '',
      serial_number: item.serial_number ?? '',
      location: item.location ?? '',
      status: normalizeApiStatus(item.status),
      provider: item.provider ?? '',
      manufacturer_invoice: item.manufacturer_invoice ?? '',
      remito: item.remito ?? '',
      manufacture_date: item.manufacture_date ?? null,
      expiration_date: item.expiration_date ?? null,
      quality_control_date: item.quality_control_date ?? null,
      operation_verification_date: item.operation_verification_date ?? null,
      accessories_check_date: item.accessories_check_date ?? null,
      created_at: item.created_at,
      inspection_forms: item.inspection_forms ?? null,
      deletedAt: item.deleted_at ?? null,
    }));
  }, []);

  const syncInspectionDefaults = useCallback(
    (
      form: InventoryForm,
      product: ProductSummary | undefined,
      existing: InventoryInspectionForms | null | undefined,
    ) => {
      if (!product) {
        return existing ?? emptyInspectionForms();
      }
      const defaults = buildDefaultInspectionForms({
        batch_number: form.batch_number,
        serial_number: form.serial_number,
        provider: form.provider,
        manufacturer_invoice: form.manufacturer_invoice,
        expiration_date: form.expiration_date,
        visual_validation_date: form.visual_validation_date,
        functionality_verification_date: form.functionality_verification_date,
        product_name: product.name,
        regulatory_class: product.regulatory_class,
        model_number: product.model_number,
      });
      return {
        quality_release: {
          ...(existing?.quality_release ?? {}),
          ...defaults.quality_release,
        },
        functionality_verification: {
          ...(existing?.functionality_verification ?? {}),
          ...defaults.functionality_verification,
        },
      };
    },
    [],
  );

  const fetchInventory = useCallback(async () => {
    if (!actualEnterprise?.id) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/all_inventory/${actualEnterprise.id}`,
        {
          method: 'GET',
          headers: authHeaders,
          credentials: 'include',
        },
      );
      if (!response.ok) {
        console.error('Failed to load inventory:', response.status);
        setInventoryRows([]);
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        setInventoryRows([]);
        return;
      }
      setInventoryRows(mapInventoryRows(data));
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setInventoryRows([]);
    }
  }, [actualEnterprise?.id, authHeaders, mapInventoryRows]);

  const refreshProductOptions = useCallback((products: ProductSummary[]) => {
    setProductCatalog(products);
    const options = products
      .map((product) => ({
        value: product.id,
        label: product.barcode
          ? `${product.name} (${product.barcode})`
          : product.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setProductOptions(options);
  }, []);

  const getProducts = useCallback(async () => {
    if (!actualEnterprise?.id) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/inventory_products/${actualEnterprise.id}`,
        {
          method: 'GET',
          headers: authHeaders,
          credentials: 'include',
        },
      );
      if (!response.ok) {
        console.error('Failed to load products for inventory:', response.status);
        refreshProductOptions([]);
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        refreshProductOptions([]);
        return;
      }
      refreshProductOptions(data as ProductSummary[]);
    } catch (err) {
      console.error('Failed to load products for inventory:', err);
      refreshProductOptions([]);
    }
  }, [actualEnterprise?.id, authHeaders, refreshProductOptions]);

  useEffect(() => {
    fetchInventory();
    getProducts();
  }, [fetchInventory, getProducts]);

  useEffect(() => {
    document.title = t('inventory.documentTitle');
  }, [t, i18n.language]);

  const selectedProduct = useMemo(
    () => productCatalog.find((product) => product.id === inventoryForm.product_id),
    [productCatalog, inventoryForm.product_id],
  );

  const availableStatusOptions = useMemo(
    () => statusOptionsForContext(t, selectedProduct, inventoryForm),
    [t, i18n.language, selectedProduct, inventoryForm],
  );

  const handleInspectionToggle = (
    field:
      | 'visual_validation_complete'
      | 'functionality_verification_complete'
      | 'accessories_check_complete',
    checked: boolean,
  ) => {
    setInventoryForm((prev) => {
      let next: InventoryForm;
      if (field === 'visual_validation_complete') {
        next = {
          ...prev,
          visual_validation_complete: checked,
          visual_validation_date: checked
            ? prev.visual_validation_date ?? todayAtMidnight()
            : null,
        };
      } else if (field === 'functionality_verification_complete') {
        next = {
          ...prev,
          functionality_verification_complete: checked,
          functionality_verification_date: checked
            ? prev.functionality_verification_date ?? todayAtMidnight()
            : null,
        };
      } else {
        next = {
          ...prev,
          accessories_check_complete: checked,
          accessories_check_date: checked
            ? prev.accessories_check_date ?? todayAtMidnight()
            : null,
        };
      }
      const product = productCatalog.find((item) => item.id === next.product_id);
      const resolved = resolveStatusForProduct(t, next, product);
      if (
        field === 'visual_validation_complete' ||
        field === 'functionality_verification_complete'
      ) {
        setInspectionForms((current) => syncInspectionDefaults(resolved, product, current));
      }
      return resolved;
    });
  };

  const handleProductChange = (value: string | null) => {
    const productId = value ?? '';
    const product = productCatalog.find((item) => item.id === productId);
    setInventoryForm((prev) => {
      const nextForm = reconcileProductSelection(t, prev, product, productId);
      setInspectionForms((current) =>
        syncInspectionDefaults(nextForm, product, current),
      );
      return nextForm;
    });
  };

  const isFormValid = () => {
    if (inventoryForm.product_id.trim() === '') {
      return false;
    }
    if (validateInventoryIdentifiers(t, inventoryForm, serialInputMode, Boolean(editingItem)) !== null) {
      return false;
    }
    return validateInspectionChecks(t, inventoryForm, selectedProduct) === null;
  };

  const openModal = (row?: InventoryRow) => {
    if (row) {
      setEditingItem(row);
      setSerialInputMode('single');
      const inspectionFields = resolveInspectionFields(row);
      const product = productCatalog.find((item) => item.id === row.product_id);
      const openedForm = resolveStatusForProduct(
        t,
        {
          product_id: row.product_id,
          batch_number: digitsOnly(row.batch_number),
          serial_number: digitsOnly(row.serial_number),
          serial_number_end: '',
          location: row.location,
          status: normalizeApiStatus(row.status),
          provider: row.provider,
          manufacturer_invoice: row.manufacturer_invoice,
          remito: row.remito,
          manufacture_date: parseApiDate(row.manufacture_date),
          expiration_date: parseApiDate(row.expiration_date),
          visual_validation_complete: inspectionFields.visual_validation_complete,
          visual_validation_date: inspectionFields.visual_validation_date,
          functionality_verification_complete: inspectionFields.functionality_verification_complete,
          functionality_verification_date: inspectionFields.functionality_verification_date,
          accessories_check_complete: inspectionFields.accessories_check_complete,
          accessories_check_date: inspectionFields.accessories_check_date,
        },
        product,
      );
      setInventoryForm(openedForm);
      setInspectionForms(
        syncInspectionDefaults(openedForm, product, row.inspection_forms),
      );
    } else {
      setEditingItem(null);
      setSerialInputMode('single');
      setInventoryForm(initialForm);
      setInspectionForms(emptyInspectionForms());
    }
    setErrorMessage('');
    setImportErrors([]);
    setImportNotification(null);
    setAutoImportFile(null);
    setModalOpened(true);
  };

  const buildPayload = () => {
    const normalizedForm = resolveStatusForProduct(t, inventoryForm, selectedProduct);
    const mergedInspectionForms = syncInspectionDefaults(
      normalizedForm,
      selectedProduct,
      inspectionForms,
    );
    const payload: Record<string, unknown> = {
      product_id: normalizedForm.product_id,
      batch_number: normalizedForm.batch_number || null,
      serial_number: normalizedForm.serial_number || null,
      location: normalizedForm.location || null,
      status: normalizedForm.status,
      provider: normalizedForm.provider || null,
      manufacturer_invoice: normalizedForm.manufacturer_invoice || null,
      remito: usesRemito(normalizedForm.status) ? normalizedForm.remito || null : null,
      manufacture_date: formatApiDate(normalizedForm.manufacture_date),
      expiration_date: formatApiDate(normalizedForm.expiration_date),
      quality_control_date: normalizedForm.visual_validation_complete
        ? formatApiDate(normalizedForm.visual_validation_date)
        : null,
      operation_verification_date: normalizedForm.functionality_verification_complete
        ? formatApiDate(normalizedForm.functionality_verification_date)
        : null,
      accessories_check_date: normalizedForm.accessories_check_complete
        ? formatApiDate(normalizedForm.accessories_check_date)
        : null,
      inspection_forms: mergedInspectionForms,
    };
    if (!editingItem && serialInputMode === 'range' && inventoryForm.serial_number_end) {
      payload.serial_number_end = inventoryForm.serial_number_end;
    }
    return payload;
  };

  const handleStatusChange = (value: string | null) => {
    const status = (value as InventoryStatus) ?? 'APPROVED';
    setInventoryForm((prev) => ({
      ...prev,
      status,
      remito: usesRemito(status) ? prev.remito : '',
    }));
  };

  const updateInventoryField = (patch: Partial<InventoryForm>) => {
    setInventoryForm((prev) => {
      const nextForm = { ...prev, ...patch };
      if (
        patch.batch_number !== undefined ||
        patch.serial_number !== undefined ||
        patch.provider !== undefined ||
        patch.manufacturer_invoice !== undefined ||
        patch.expiration_date !== undefined ||
        patch.visual_validation_date !== undefined ||
        patch.functionality_verification_date !== undefined
      ) {
        setInspectionForms((current) =>
          syncInspectionDefaults(nextForm, selectedProduct, current),
        );
      }
      return nextForm;
    });
  };

  const persistInventory = async (closeOnSuccess = true): Promise<string | false> => {
    const validationError = validateInventoryIdentifiers(
      t,
      inventoryForm,
      serialInputMode,
      Boolean(editingItem),
    );
    if (validationError) {
      setErrorMessage(validationError);
      return false;
    }
    const inspectionError = validateInspectionChecks(t, inventoryForm, selectedProduct);
    if (inspectionError) {
      setErrorMessage(inspectionError);
      return false;
    }

    const url = editingItem
      ? `${Environment.VITE_API_URL}/api/v1/inventory/${editingItem.uuid}`
      : `${Environment.VITE_API_URL}/api/v1/inventory/`;
    const method = editingItem ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify(buildPayload()),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setErrorMessage(formatApiError(body.detail) ?? t('inventory.saveFailed'));
        return false;
      }
      const saved = await response.json();
      if (!editingItem) {
        setEditingItem({
          id: 0,
          uuid: saved.id,
          product_id: saved.product_id,
          product_name: selectedProduct?.name ?? '',
          batch_number: saved.batch_number ?? '',
          serial_number: saved.serial_number ?? '',
          location: saved.location ?? '',
          status: saved.status,
          provider: saved.provider ?? '',
          manufacturer_invoice: saved.manufacturer_invoice ?? '',
          remito: saved.remito ?? '',
          manufacture_date: saved.manufacture_date ?? null,
          expiration_date: saved.expiration_date ?? null,
          quality_control_date: saved.quality_control_date ?? null,
          operation_verification_date: saved.operation_verification_date ?? null,
          accessories_check_date: saved.accessories_check_date ?? null,
          created_at: saved.created_at,
          inspection_forms: saved.inspection_forms ?? null,
        });
        setInventoryForm((prev) => ({
          ...prev,
          status: saved.status as InventoryStatus,
        }));
      }
      if (closeOnSuccess) {
        setModalOpened(false);
      }
      fetchInventory();
      return saved.id as string;
    } catch {
      setErrorMessage(t('inventory.saveFailed'));
      return false;
    }
  };

  const saveInventory = async () => {
    await persistInventory(true);
  };

  const handleGenerateDocument = async (
    documentType: 'quality-release' | 'functionality-verification',
  ) => {
    if (!accessToken) {
      return;
    }
    setGeneratingDocument(documentType);
    setErrorMessage('');
    try {
      const inventoryId = await persistInventory(false);
      if (!inventoryId) {
        return;
      }
      await downloadInventoryDocument(inventoryId, documentType, accessToken);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : t('inventory.generateDocumentFailed'),
      );
    } finally {
      setGeneratingDocument(null);
    }
  };

  const showQualityDocumentButton =
    Boolean(selectedProduct?.visual_validation) &&
    inventoryForm.visual_validation_complete &&
    Boolean(inventoryForm.visual_validation_date);

  const showFunctionalityDocumentButton =
    Boolean(selectedProduct?.functionality_verification) &&
    inventoryForm.functionality_verification_complete &&
    Boolean(inventoryForm.functionality_verification_date);

  const deleteInventory = async () => {
    if (!editingItem) {
      return;
    }
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/inventory/${editingItem.uuid}`,
        {
          method: 'DELETE',
          headers: authHeaders,
          credentials: 'include',
        },
      );
      if (!response.ok) {
        setErrorMessage(t('inventory.deleteFailed'));
        return;
      }
      setModalOpened(false);
      fetchInventory();
    } catch {
      setErrorMessage(t('inventory.deleteFailed'));
    }
  };

  const inventoryImportHeaders = useMemo(
    () => ({
      product_id: t('inventory.product'),
      batch_number: t('inventory.lotNumber'),
      serial_number: t('inventory.serial'),
      location: t('inventory.location'),
      status: t('common.status'),
      provider: t('inventory.provider'),
      manufacturer_invoice: t('inventory.manufacturerInvoice'),
      remito: t('inventory.remito'),
      manufacture_date: t('inventory.manufactureDate'),
      expiration_date: t('inventory.expirationDate'),
    }),
    [t, i18n.language],
  );

  const resolveInventoryStatus = (raw: unknown): InventoryStatus => {
    const text = String(raw ?? '').trim();
    if (!text) return 'APPROVED';
    const byValue = generalStatusOptions(t).find((option) => option.value === text);
    if (byValue) return byValue.value;
    const byLabel = generalStatusOptions(t).find(
      (option) => option.label.toLowerCase() === text.toLowerCase(),
    );
    return (byLabel?.value ?? text) as InventoryStatus;
  };

  const resolveProductId = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const byId = productCatalog.find((product) => product.id === text);
    if (byId) return byId.id;
    const byName = productCatalog.find(
      (product) => product.name.toLowerCase() === text.toLowerCase(),
    );
    return byName?.id ?? text;
  };

  const rowToInventoryForm = (row: Record<string, unknown>): InventoryForm => ({
    product_id: resolveProductId(readRowCell(row, inventoryImportHeaders.product_id, 'Product', 'product_id')),
    batch_number: digitsOnly(String(readRowCell(row, inventoryImportHeaders.batch_number, 'Lot') ?? '')),
    serial_number: digitsOnly(String(readRowCell(row, inventoryImportHeaders.serial_number, 'Serial') ?? '')),
    serial_number_end: '',
    location: String(readRowCell(row, inventoryImportHeaders.location, 'Location') ?? ''),
    status: resolveInventoryStatus(readRowCell(row, inventoryImportHeaders.status, 'Status')),
    provider: String(readRowCell(row, inventoryImportHeaders.provider, 'Provider') ?? ''),
    manufacturer_invoice: String(readRowCell(row, inventoryImportHeaders.manufacturer_invoice, 'Manufacturer Invoice') ?? ''),
    remito: String(readRowCell(row, inventoryImportHeaders.remito, 'Remito') ?? ''),
    manufacture_date: parseApiDate(
      readRowCell(row, inventoryImportHeaders.manufacture_date, 'Manufacture Date')
        ? String(readRowCell(row, inventoryImportHeaders.manufacture_date, 'Manufacture Date'))
        : null,
    ),
    expiration_date: parseApiDate(
      readRowCell(row, inventoryImportHeaders.expiration_date, 'Expiration Date')
        ? String(readRowCell(row, inventoryImportHeaders.expiration_date, 'Expiration Date'))
        : null,
    ),
    visual_validation_complete: false,
    visual_validation_date: null,
    functionality_verification_complete: false,
    functionality_verification_date: null,
    accessories_check_complete: false,
    accessories_check_date: null,
  });

  const inventoryFormToPayload = (form: InventoryForm) => {
    const product = productCatalog.find((item) => item.id === form.product_id);
    const normalizedForm = resolveStatusForProduct(t, form, product);
    return {
      product_id: normalizedForm.product_id,
      batch_number: normalizedForm.batch_number || null,
      serial_number: normalizedForm.serial_number || null,
      location: normalizedForm.location || null,
      status: normalizedForm.status,
      provider: normalizedForm.provider || null,
      manufacturer_invoice: normalizedForm.manufacturer_invoice || null,
      remito: usesRemito(normalizedForm.status) ? normalizedForm.remito || null : null,
      manufacture_date: formatApiDate(normalizedForm.manufacture_date),
      expiration_date: formatApiDate(normalizedForm.expiration_date),
      quality_control_date: null,
      operation_verification_date: null,
      accessories_check_date: null,
      inspection_forms: emptyInspectionForms(),
    };
  };

  const validateImportInventoryForm = (form: InventoryForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!form.product_id.trim()) rowErrors.push(t('inventory.product'));
    const identifierError = validateInventoryIdentifiers(t, form, 'single', true);
    if (identifierError) rowErrors.push(identifierError);
    if (rowErrors.length > 0) {
      return [`Line ${line}: ${rowErrors.join(' ')}`];
    }
    return [];
  };

  const executeInventoryImport = async (
    createForms: InventoryForm[],
    updateEntries: Array<{ id: string; form: InventoryForm }>,
    deleteIds: string[],
  ) => {
    for (const form of createForms) {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/inventory/`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify(inventoryFormToPayload(form)),
      });
      if (!response.ok) {
        setImportNotification({ message: t('common.import.failed'), color: 'red' });
        return;
      }
    }

    const ok = await executeImportUpsert({
      createPayloads: [],
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: inventoryFormToPayload(entry.form),
      })),
      deleteIds,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/inventory/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/inventory/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: authHeaders,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setImportNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setImportNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    fetchInventory();
  };

  const handleAutoImport = async () => {
    if (!autoImportFile) return;
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(inventoryRows.map((row) => row.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const headerToFormKey = Object.fromEntries(
        Object.entries(inventoryImportHeaders).map(([formKey, header]) => [header, formKey]),
      ) as Record<string, keyof InventoryForm & string>;

      const createForms: InventoryForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const form = rowToInventoryForm(row);
        errors.push(...validateImportInventoryForm(form, line));
        createForms.push(form);
      }

      const updateEntries: Array<{ id: string; form: InventoryForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = inventoryRows.find((row) => row.uuid === entry.id);
        if (!existing) continue;
        const patch = rowToInventoryForm(entry.row);
        const inspectionFields = resolveInspectionFields(existing);
        const merged = mergeDefinedFields(
          {
            product_id: existing.product_id,
            batch_number: digitsOnly(existing.batch_number),
            serial_number: digitsOnly(existing.serial_number),
            serial_number_end: '',
            location: existing.location,
            status: normalizeApiStatus(existing.status),
            provider: existing.provider,
            manufacturer_invoice: existing.manufacturer_invoice,
            remito: existing.remito,
            manufacture_date: parseApiDate(existing.manufacture_date),
            expiration_date: parseApiDate(existing.expiration_date),
            visual_validation_complete: inspectionFields.visual_validation_complete,
            visual_validation_date: inspectionFields.visual_validation_date,
            functionality_verification_complete: inspectionFields.functionality_verification_complete,
            functionality_verification_date: inspectionFields.functionality_verification_date,
            accessories_check_complete: inspectionFields.accessories_check_complete,
            accessories_check_date: inspectionFields.accessories_check_date,
          },
          patch,
          formKeysPresentInRow(entry.row, headerToFormKey),
        );
        errors.push(...validateImportInventoryForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setImportNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeInventoryImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error(error);
      setImportNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const columns = useMemo<MRT_ColumnDef<InventoryRow>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'product_name',
        header: t('inventory.product'),
        mantineTableHeadCellProps: {
          sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' },
        },
      },
      {
        accessorKey: 'serial_number',
        header: t('inventory.serial'),
        mantineTableHeadCellProps: {
          sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' },
        },
      },
      {
        accessorKey: 'batch_number',
        header: t('inventory.lotNumber'),
        mantineTableHeadCellProps: {
          sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' },
        },
      },
      {
        accessorKey: 'location',
        header: t('inventory.location'),
        mantineTableHeadCellProps: {
          sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' },
        },
      },
      {
        accessorKey: 'status',
        header: t('common.status'),
        Cell: ({ cell }) =>
          inventoryStatusLabel(t, cell.getValue<InventoryStatus | 'AVAILABLE'>()) ??
          cell.getValue<string>(),
        mantineTableHeadCellProps: {
          sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' },
        },
      },
      {
        accessorKey: 'product_id',
        header: t('inventory.product'),
        enableHiding: true,
      },
    ],
      ['product_name', 'serial_number', 'batch_number', 'location'],
    ),
    [t, i18n.language],
  );

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setImportNotification(null);
  };

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: inventoryRows,
      mantineTableProps: { sx: { borderCollapse: 'separate', borderSpacing: '2px', '& td': { borderRadius: '4px' } } },
      enableColumnActions: false,
      localization: mrtLocalization,
      enableColumnFilters: true,
      enableSorting: true,
      enablePagination: true,
      initialState: {
        density: 'sm' as MRT_DensityState,
        showGlobalFilter: true,
        columnVisibility: { product_id: false },
        pagination: { pageSize: 20, pageIndex: 0 },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<InventoryRow> }) => ({
        onDoubleClick: () => openModal(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'inventory',
      sheetName: 'Inventory',
      getDeletedValue: (row) => Boolean(row.deletedAt),
      getCellValue: (row, columnId) => {
        if (columnId === 'status') {
          return inventoryStatusLabel(t, row.status);
        }
        if (columnId === 'product_id') {
          return row.product_id;
        }
        return row[columnId as keyof InventoryRow];
      },
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={importNotification}
    />
  );

  return (
    <>
      <Group position="apart" align="center" mb="md">
        <Title order={1}>{t('inventory.title')}</Title>
        <Button onClick={() => openModal()}>{t('inventory.addItem')}</Button>
      </Group>

      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="inventory"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={
          editingItem
            ? t('inventory.updateItem')
            : t('inventory.addItemModal')
        }
        centered
        size="lg"
      >
        <ScrollArea h={modalScrollAreaHeight}>
          <Stack spacing="sm" p="md">
            <Select
              label={t('inventory.product')}
              placeholder={t('inventory.selectProduct')}
              searchable
              required
              data={productOptions}
              value={inventoryForm.product_id || null}
              onChange={handleProductChange}
            />

            {!editingItem ? (
              <SegmentedControl
                value={serialInputMode}
                onChange={(value) => {
                  const mode = value as SerialInputMode;
                  setSerialInputMode(mode);
                  setErrorMessage('');
                  if (mode === 'single') {
                    setInventoryForm((prev) => ({ ...prev, serial_number_end: '' }));
                  }
                }}
                data={[
                  { label: t('inventory.singleSerial'), value: 'single' },
                  { label: t('inventory.serialRange'), value: 'range' },
                ]}
              />
            ) : null}

            {editingItem || serialInputMode === 'single' ? (
              <TextInput
                label={t('inventory.serialNumber')}
                placeholder={t('inventory.digitsOnly')}
                value={inventoryForm.serial_number}
                onChange={(e) =>
                  updateInventoryField({
                    serial_number: digitsOnly(e.currentTarget.value),
                  })
                }
              />
            ) : (
              <Group grow align="flex-end">
                <TextInput
                  label={t('inventory.serialFrom')}
                  placeholder={t('inventory.serialFromPlaceholder')}
                  value={inventoryForm.serial_number}
                  onChange={(e) =>
                    setInventoryForm({
                      ...inventoryForm,
                      serial_number: digitsOnly(e.currentTarget.value),
                    })
                  }
                />
                <TextInput
                  label={t('inventory.serialTo')}
                  placeholder={t('inventory.serialToPlaceholder')}
                  value={inventoryForm.serial_number_end}
                  onChange={(e) =>
                    updateInventoryField({
                      serial_number_end: digitsOnly(e.currentTarget.value),
                    })
                  }
                />
              </Group>
            )}

            {!editingItem && serialInputMode === 'range' ? (
              <Text size="sm" color="dimmed">
                {t('inventory.serialRangeHint')}
              </Text>
            ) : null}

            <Group grow>
              <TextInput
                label={t('inventory.lotNumber')}
                placeholder={t('inventory.digitsOnly')}
                value={inventoryForm.batch_number}
                onChange={(e) =>
                  updateInventoryField({
                    batch_number: digitsOnly(e.currentTarget.value),
                  })
                }
              />
              <TextInput
                label={t('inventory.location')}
                value={inventoryForm.location}
                onChange={(e) =>
                  setInventoryForm({ ...inventoryForm, location: e.currentTarget.value })
                }
              />
            </Group>

            <Select
              label={t('common.status')}
              data={availableStatusOptions}
              value={inventoryForm.status}
              onChange={handleStatusChange}
            />

            {selectedProduct?.visual_validation ? (
              <Stack spacing="xs">
                <Checkbox
                  label={t('inventory.visualValidationComplete')}
                  checked={inventoryForm.visual_validation_complete}
                  onChange={(event) =>
                    handleInspectionToggle(
                      'visual_validation_complete',
                      event.currentTarget.checked,
                    )
                  }
                />
                {inventoryForm.visual_validation_complete ? (
                  <DateInput
                    label={t('inventory.visualValidationDate')}
                    required
                    value={inventoryForm.visual_validation_date}
                    onChange={(value) =>
                      updateInventoryField({ visual_validation_date: value })
                    }
                  />
                ) : null}
              </Stack>
            ) : null}

            {selectedProduct?.functionality_verification ? (
              <Stack spacing="xs">
                <Checkbox
                  label={t('inventory.functionalityVerificationComplete')}
                  checked={inventoryForm.functionality_verification_complete}
                  onChange={(event) =>
                    handleInspectionToggle(
                      'functionality_verification_complete',
                      event.currentTarget.checked,
                    )
                  }
                />
                {inventoryForm.functionality_verification_complete ? (
                  <DateInput
                    label={t('inventory.functionalityVerificationDate')}
                    required
                    value={inventoryForm.functionality_verification_date}
                    onChange={(value) =>
                      updateInventoryField({ functionality_verification_date: value })
                    }
                  />
                ) : null}
              </Stack>
            ) : null}

            {selectedProduct?.accessories_check ? (
              <Stack spacing="xs">
                <Checkbox
                  label={t('inventory.accessoriesCheckComplete')}
                  checked={inventoryForm.accessories_check_complete}
                  onChange={(event) =>
                    handleInspectionToggle(
                      'accessories_check_complete',
                      event.currentTarget.checked,
                    )
                  }
                />
                {inventoryForm.accessories_check_complete ? (
                  <DateInput
                    label={t('inventory.accessoriesCheckDate')}
                    required
                    value={inventoryForm.accessories_check_date}
                    onChange={(value) =>
                      setInventoryForm({ ...inventoryForm, accessories_check_date: value })
                    }
                  />
                ) : null}
              </Stack>
            ) : null}

            <Group grow>
              <TextInput
                label={t('inventory.provider')}
                value={inventoryForm.provider}
                onChange={(e) =>
                  updateInventoryField({ provider: e.currentTarget.value })
                }
              />
              <TextInput
                label={t('inventory.manufacturerInvoice')}
                value={inventoryForm.manufacturer_invoice}
                onChange={(e) =>
                  updateInventoryField({
                    manufacturer_invoice: e.currentTarget.value,
                  })
                }
              />
            </Group>

            <TextInput
              label={t('inventory.remito')}
              description={
                usesRemito(inventoryForm.status)
                  ? t('inventory.remitoAutoHint')
                  : t('inventory.remitoManualHint')
              }
              value={inventoryForm.remito}
              disabled={!usesRemito(inventoryForm.status)}
              onChange={(e) =>
                setInventoryForm({ ...inventoryForm, remito: e.currentTarget.value })
              }
            />

            <Group grow>
              <DateInput
                label={t('inventory.manufactureDate')}
                value={inventoryForm.manufacture_date}
                onChange={(value) =>
                  setInventoryForm({ ...inventoryForm, manufacture_date: value })
                }
              />
              <DateInput
                label={t('inventory.expirationDate')}
                value={inventoryForm.expiration_date}
                onChange={(value) =>
                  updateInventoryField({ expiration_date: value })
                }
              />
            </Group>

            <InventoryInspectionFormsPanel
              forms={inspectionForms}
              showQualityRelease={Boolean(selectedProduct?.visual_validation)}
              showFunctionalityVerification={Boolean(
                selectedProduct?.functionality_verification,
              )}
              onChange={setInspectionForms}
            />

            {errorMessage ? (
              <Text color="red" size="sm">
                {errorMessage}
              </Text>
            ) : null}
          </Stack>
        </ScrollArea>

        <Group position="right" p="md">
          {showQualityDocumentButton ? (
            <Button
              variant="outline"
              loading={generatingDocument === 'quality-release'}
              onClick={() => handleGenerateDocument('quality-release')}
              title={t('inventory.saveBeforeDocumentTooltip')}
            >
              {t('inventory.generateQualityRelease')}
            </Button>
          ) : null}
          {showFunctionalityDocumentButton ? (
            <Button
              variant="outline"
              loading={generatingDocument === 'functionality-verification'}
              onClick={() => handleGenerateDocument('functionality-verification')}
              title={t('inventory.saveBeforeDocumentTooltip')}
            >
              {t('inventory.generateFunctionalityVerification')}
            </Button>
          ) : null}
          {editingItem ? (
            <Button color="red" variant="outline" onClick={deleteInventory}>
              {t('common.delete')}
            </Button>
          ) : null}
          <Button onClick={saveInventory} disabled={!isFormValid()}>
            {editingItem ? t('common.update') : t('common.save')}
          </Button>
        </Group>
      </Modal>
    </>
  );
}
